
// NO IMPORTS from firebase here. This file must remain pure to prevent circular dependencies.

declare global {
    const __APP_VERSION__: string;
    interface Window {
        adsbygoogle: any[];
    }
}

// Manually defined interface compatible with Firebase Timestamp
// This allows the app to load types without triggering Firebase initialization code.
export interface Timestamp {
    toMillis(): number;
    toDate(): Date;
    seconds: number;
    nanoseconds: number;
    isEqual(other: any): boolean;
}

// Single source of truth for the default theme color
export const DEFAULT_THEME_COLOR = 'hsl(259, 55%, 20%)';

// Pages Enum
export enum Page {
    Landing = 'Landing',
    Login = 'Login',
    Signup = 'Signup',
    Home = 'Home',
    Search = 'Search',
    Categories = 'Categories',
    FeaturedBrands = 'FeaturedBrands',
    Notifications = 'Notifications',
    Groups = 'Groups',
    Profile = 'Profile',
    Settings = 'Settings',
    PostDetail = 'PostDetail',
    ChatList = 'ChatList',
    ChatConversation = 'ChatConversation',
    FilteredFeed = 'FilteredFeed',
    GameDetail = 'GameDetail',
    FriendsList = 'FriendsList',
    Leaderboard = 'Leaderboard',
    ClueStore = 'ClueStore',
    PrivacyPolicy = 'PrivacyPolicy',
    TermsOfService = 'TermsOfService',
    Admin = 'Admin',
    DailyChallenge = 'DailyChallenge',
    RecommendedGroups = 'RecommendedGroups',
    BlockedUsers = 'BlockedUsers',
    PrivateGame = 'PrivateGame',
    AIHome = 'AIHome',
    AISupport = 'AISupport',
    CreatePost = 'CreatePost',
    ImageEditor = 'ImageEditor',
    GuessResult = 'GuessResult',
    AboutUs = 'AboutUs',
    ManageGroup = 'ManageGroup',
    AICreditStorePage = 'AICreditStorePage',
    InviteFriends = 'InviteFriends', 
}

// Page Navigation
export interface PageState {
    page: Page;
    title: string;
    props?: any;
    scrollPosition?: number;
}

export interface PostContext {
    clue: string;
    answer: string;
    category: string;
    postColor?: string;
    imageUrl?: string;
}

export interface PageContextType {
    pageStack: PageState[];
    switchPage: (page: Page, title: string, props?: any) => void;
    goBack: () => void;
    scrollToTop: (smooth?: boolean) => void;
    launchEditProfileModal: (triggerRect: DOMRect) => void;
    updatePageStackProps: (index: number, newProps: any) => void;
    launchImageEditor: (
        type: 'avatar' | 'background' | 'post',
        imageUrl: string,
        currentEdits: ImageEdits,
        previewShape: 'circle' | 'square',
        onSave: (newEdits: ImageEdits, newImageUrl?: string, newPostColor?: string) => void,
        triggerRect: DOMRect | null,
        postContext?: PostContext
    ) => void;
    launchCelebrationModal: (puzzle: Puzzle, suggestions?: Puzzle[]) => void;
    launchGuessersModal: (title: string, userIds: string[], extraInfo?: { userId: string, subText: string }[]) => void;
}

export interface AppConfig {
    logoUrl: string;
}

export interface AppContextType {
    appConfig: AppConfig;
    updateAppConfig: (newConfig: Partial<AppConfig>) => Promise<void>;
    puzzlesSolvedCount: number;
    incrementPuzzlesSolvedCount: () => void;
    resetPuzzlesSolvedCount: () => void;
}

export interface ImageEdits {
    adjustments: {
        brightness: number;
        contrast: number;
        saturate: number;
        blur: number;
        zoom: number;
        offsetX: number;
        offsetY: number;
    };
    filter: string;
    frame: string;
    effect: string;
}

export const defaultImageEdits: ImageEdits = {
    adjustments: {
        brightness: 100,
        contrast: 100,
        saturate: 100,
        blur: 0,
        zoom: 1,
        offsetX: 0,
        offsetY: 0,
    },
    filter: 'none',
    frame: 'none',
    effect: 'none',
};

export const getSafeImageEdits = (edits?: Partial<ImageEdits> | null): ImageEdits => {
    if (!edits) return defaultImageEdits;
    return {
        ...defaultImageEdits,
        ...edits,
        adjustments: {
            ...defaultImageEdits.adjustments,
            ...(edits.adjustments || {})
        }
    };
};

export interface UserProfile {
    uid: string;
    displayName: string;
    username?: string; 
    photoURL: string;
    bio?: string;
    dob: string;
    score: number;
    clues: number;
    following: string[];
    followers: string[];
    solvedPuzzles: string[];
    revealedClues?: { [puzzleId: string]: number[] };
    isPrivate: boolean;
    requestType: 'follow' | 'friend' | 'both';
    primaryColor: string;
    postColor: string;
    correctGuessSound: string;
    wrongGuessSound: string;
    uiClickSound: string;
    uiNavigationSound: string;
    uiNotificationSound: string;
    uiClueRevealSound: string;
    backgroundMusic: string;
    enableShimmerEffect: boolean;
    backgroundUrl?: string;
    photoEdits?: ImageEdits;
    backgroundEdits?: ImageEdits;
    isAdmin?: boolean;
    isBanned?: boolean;
    blockedUsers?: string[];
    isFaceLoginEnabled?: boolean;
    faceLoginData?: string;
    aiCredits: number;
    email?: string;
    lastFreeCreditClaimed?: Timestamp;
    isOnline?: boolean;
    lastSeen?: Timestamp;
    appearOffline?: boolean;
    hasCompletedOnboarding?: boolean;
    inAppNotificationsEnabled?: boolean;
    correctGuessTheme?: string;
    incorrectGuessTheme?: string;
    isBusiness?: boolean;
    businessWebsite?: string;
    businessActionText?: string;
    aiConsentEnabled?: boolean;
    createdAt?: any;
}

export interface Reaction {
    [emoji: string]: string[];
}

export interface Comment {
    id: string;
    userId: string;
    username?: string;
    userDisplayName: string;
    userPhotoURL: string;
    text: string;
    imageUrl?: string;
    createdAt: Timestamp;
    reactions?: Reaction;
    mentions?: { username: string; uid: string; }[];
    replyToId?: string;
    replyingToUserId?: string;
}

export interface Puzzle {
    id: string;
    creatorId: string;
    creatorProfile: {
        displayName: string;
        photoURL: string;
        postColor?: string;
    };
    imageUrl: string;
    audioUrl?: string; // S3 or Storage link for the audio puzzle
    audioStartTime?: number; // Slice start time in seconds
    audioEndTime?: number; // Slice end time in seconds
    answerAudioStartTime?: number; // Slice start time for correct guess reveals
    answerAudioEndTime?: number; // Slice end time for correct guess reveals
    clue: string;
    answer: string;
    category: string;
    createdAt: Timestamp;
    likes: string[];
    dislikes: string[];
    hearts: string[];
    comments: Comment[];
    imageEdits?: ImageEdits;
    postColor?: string;
    groupId?: string;
    type?: 'standard' | 'timed' | 'ai' | 'daily';
    showInMainFeed?: boolean;
    gameStartTime?: Timestamp;
    timerDuration?: number;
    playerScores?: { [userId: string]: { score: number; completedAt: Timestamp } };
    showClue?: boolean;
    areCluesAllowed?: boolean;
    prompt?: string;
    correctGuessers?: string[];
    incorrectGuessers?: string[];
    incorrectGuesses?: { userId: string; guess: string; timestamp: number }[];
    hashtags?: string[];
    generatedByAI?: boolean;
    dailyDate?: string;
    isSponsored?: boolean;
    promoLink?: string;
    promoCoupon?: string;
}

export interface GroupGame {
    id: string;
    name: string;
    creatorId: string;
    members: string[];
    admins: string[];
    privacy: 'public' | 'private' | 'ask-to-join';
    imageUrl?: string;
    imageEdits?: ImageEdits;
    backgroundUrl?: string;
    backgroundEdits?: ImageEdits;
    description: string;
    puzzles: string[];
    scores: { [userId: string]: number };
    solvedPuzzles: { [userId: string]: string[] };
    joinRequests: string[];
    inviteCode?: string;
    showLeaderboard?: boolean;
}

export type NotificationType = 
    | 'like' 
    | 'heart'
    | 'comment' 
    | 'comment_reaction'
    | 'comment_reply'
    | 'mention_in_comment'
    | 'follow' 
    | 'friend_request'
    | 'friend_request_accepted'
    | 'group_join_request' 
    | 'group_join_accepted'
    | 'guess_correct' 
    | 'guess_incorrect'
    | 'system'
    | 'guess_received' 
    | 'new_post_friend'
    | 'new_message'
    | 'social_guess'
    | 'mention_in_post'
    | 'mention_in_comment'
    | 'leaderboard_pass'
    | 'post_under_review'
    | 'group_deleted'
    | 'group_invite';

export interface Notification {
    id: string;
    type: NotificationType;
    fromUser?: { id: string, name: string, avatar: string };
    group?: { id: string, name: string };
    text: string;
    puzzle?: { id: string, clue: string, imageUrl: string };
    time: Timestamp;
    read: boolean;
}

export interface ReportedPuzzle {
    reportId: string;
    puzzle: Puzzle;
    reason: string;
    details: string;
    reportedAt: Timestamp;
}

export interface ReportedUser {
    reportId: string;
    user: UserProfile;
    reason: string;
    details: string;
    reportedAt: Timestamp;
}

export interface ReportedMessage {
    reportId: string;
    messageId: string;
    conversationId: string;
    senderId: string;
    reason: string;
    details: string;
    reportedAt: Timestamp;
}

export interface ReportedGroup {
    reportId: string;
    groupId: string;
    groupName: string;
    creatorId: string;
    reason: string;
    details: string;
    reportedAt: Timestamp;
}

export interface AppProblemReport {
    reportId: string;
    reporterUid: string;
    reporterDisplayName: string;
    problemType: string;
    details: string;
    reportedAt: Timestamp;
}

export interface AIReport {
    reportId: string;
    reporterUid: string;
    reporterDisplayName: string;
    reportedAt: Timestamp;
    transcript: { role: 'user' | 'model'; text: string }[];
    category: 'Bug Report' | 'User Report' | 'Content Report' | 'Feature Request' | 'Other';
    summary: string;
    urgency: number; // 1-5
    reportedEntities?: { users?: string[]; puzzleIds?: string[] };
    status: 'new' | 'in-progress' | 'resolved';
}

export interface AIAutomationSettings {
    dailyChallengeEnabled: boolean;
    singlePostEnabled: boolean;
    singlePostIntervalHours: number;
    lastDailyRun?: Timestamp;
    lastSinglePostRun?: Timestamp;
}

export interface BannedUser {
    identifier: string;
    type: 'email' | 'username';
    bannedAt: Timestamp;
    reason?: string;
}

export interface AuthContextType {
    user: any | null; 
    userProfile: UserProfile | null;
    loading: boolean;
    usersLoading: boolean;
    allUsers: UserProfile[];
    groups: GroupGame[];
    sentGroupRequests: Set<string>;
    login: (email: string, password: string) => Promise<void>;
    signup: (email: string, password: string, displayName: string, dob: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updateSettings: (settings: Partial<UserProfile>) => void;
    updateUsername: (newUsername: string) => Promise<void>;
    addPoints: (points: number) => void;
    addPointsToUser: (uid: string, points: number) => Promise<void>;
    updateScore: (points: number, puzzle: Puzzle) => void;
    deductPoints: (points: number) => void;
    useClue: (puzzleId: string, revealedIndex: number) => void;
    addFreeClue: () => void;
    addCluesAfterNativeIAP: (amount: number) => Promise<boolean>;
    toggleFollow: (targetUserId: string) => void;
    toggleBlockUser: (targetUserId: string) => void;
    createGroup: (groupData: Omit<GroupGame, 'id' | 'creatorId' | 'members' | 'admins' | 'scores' | 'solvedPuzzles' | 'joinRequests' | 'inviteCode'>) => Promise<string | undefined>;
    updateGroup: (groupId: string, data: Partial<GroupGame>) => void;
    deleteGroup: (groupId: string, reason?: string) => Promise<void>;
    joinGame: (gameIdOrCode: string) => Promise<void>;
    requestToJoinGroup: (gameId: string) => void;
    approveJoinRequest: (gameId: string, userId: string) => void;
    declineJoinRequest: (gameId: string, userId: string) => void;
    updateGroupScore: (groupId: string, points: number, puzzleId: string) => void;
    deductGroupPoints: (groupId: string, points: number) => void;
    registerFaceLogin: (imageData: string) => Promise<void>;
    disableFaceLogin: () => Promise<void>;
    loginWithFace: (imageData: string) => Promise<UserProfile | null>;
    useAICredit: () => Promise<boolean>;
    addAICredits: (amount: number) => Promise<void>;
    markOnboardingComplete: () => Promise<void>;
    kickMember: (groupId: string, userId: string) => Promise<void>;
    resetGroupScores: (groupId: string) => Promise<void>;
    purchaseStoreItem: (type: 'clue' | 'credit' | 'business', amount: number, cost: string) => Promise<boolean>;
    deleteAccount: () => Promise<void>;
    upgradeToBusiness: () => Promise<void>;
}

export interface AdminContextType {
    notifications: Notification[];
    sentRequests: Set<string>;
    addNotification: (targetUserId: string, notificationData: Omit<Notification, 'id' | 'time' | 'read'>) => Promise<void>;
    sendFriendRequest: (targetUserId: string) => void;
    sendDirectNotification: (targetUserId: string, notificationData: Omit<Notification, 'id' | 'time' | 'read'>) => void;
    reportedUsers: ReportedUser[];
    aiReports: AIReport[];
    submitAIReport: (report: Omit<AIReport, 'reportId' | 'status'>) => Promise<void>;
    reportUser: (userToReport: UserProfile, reason: string, details: string) => void;
    dismissUserReport: (reportId: string) => void;
    resolveAIReport: (reportId: string) => Promise<void>;
    refreshNotifications: () => Promise<void>;
    banUser: (uid: string) => Promise<void>;
    unbanUser: (uid: string) => Promise<void>;
    deleteUser: (uid: string) => Promise<void>;
    banAndWipeUser: (user: UserProfile, reason?: string) => Promise<void>;
    resetLeaderboard: () => Promise<void>;
    sendBroadcastMessage: () => Promise<void>;
    markAllNotificationsAsRead: () => Promise<void>;
    markSingleNotificationAsRead: (notificationId: string) => Promise<void>;
    removeNotification: (notificationId: string) => Promise<void>;
    giveCluesToUser: (uid: string, amount: number) => Promise<void>;
    giveAICreditsToUser: (uid: string, amount: number) => Promise<void>;
    reportGroup: (group: GroupGame, reason: string, details: string) => void;
    toggleBusinessStatus: (uid: string, isBusiness: boolean) => Promise<void>;
    compensateUser: (username: string, points: number, aiCredits: number, clues: number, message: string) => Promise<void>;
}

export interface PuzzleContextType {
    puzzles: Puzzle[];
    puzzlesLoading: boolean;
    reportedPuzzles: ReportedPuzzle[];
    savePuzzle: (puzzleData: Omit<Puzzle, 'id' | 'createdAt' | 'creatorId' | 'creatorProfile'>, puzzleId?: string) => Promise<string | undefined>;
    deletePuzzle: (puzzleId: string) => void;
    reportPuzzle: (puzzleId: string, reason: string, details: string) => void;
    reinstatePuzzle: (reportId: string) => void;
    deleteReportedPuzzle: (reportId: string) => void;
    refreshPuzzles: () => Promise<void>;
    recordPlayerScore: (puzzleId: string, userId: string, score: number, completedAt: Timestamp) => void;
    sharePuzzleToFeed: (puzzleId: string) => void;
    repostPuzzle: (puzzleId: string) => void;
    updatePuzzleReactions: (puzzleId: string, reactionType: 'likes' | 'dislikes' | 'hearts', userId: string, isAdding: boolean) => void;
    addCommentToPuzzle: (puzzleId: string, comment: Omit<Puzzle['comments'][0], 'id'>) => void;
    deleteComment: (puzzleId: string, commentId: string, currentComments: Comment[]) => Promise<void>;
    recordGuessAttempt: (puzzleId: string, userId: string, isCorrect: boolean, guess?: string) => void;
    addReactionToComment: (puzzleId: string, commentId: string, emoji: string) => Promise<void>;
}

export interface ChatMessage {
    id: string;
    conversationId: string;
    senderId: string;
    text?: string;
    imageUrl?: string;
    createdAt: Timestamp;
    read: boolean;
    edited?: boolean;
    reactions?: Reaction;
}

export interface ChatConversation {
    id: string;
    participants: string[];
    lastMessage?: ChatMessage;
}

export interface ChatContextType {
    conversations: ChatConversation[];
    messages: { [key: string]: ChatMessage[] };
    totalUnreadCount: number;
    sendMessage: (conversationId: string, message: { text?: string; imageUrl?: string | null }) => Promise<void>;
    editMessage: (conversationId: string, messageId: string, newText: string) => Promise<void>;
    deleteMessage: (conversationId: string, messageId: string) => Promise<void>;
    deleteConversation: (conversationId: string) => Promise<void>;
    reportMessage: (conversationId: string, messageId: string, senderId: string, reason: string, details: string) => Promise<void>;
    getOrCreateConversation: (recipientId: string) => Promise<string>;
    markAsRead: (conversationId: string) => void;
    addReactionToMessage: (conversationId: string, messageId: string, emoji: string) => Promise<void>;
}

export interface MockAd {
    brandName: string;
    brandLogo: string;
    slogan: string;
    callToAction: string;
    imageUrl: string;
    link: string;
}

export interface PresetTheme {
    name: string;
    primary: string;
    post: string;
}
